// Guvnor billing on the contract page (Session BBZ, Director instruction 12 October 2026). A contract switched to Guvnor
// billing is billed by the platform, which raises each period's Xero DRAFT itself, priced at the contract's price on
// the invoice date, instead of by a Xero repeating invoice (which Xero's API cannot change when the price moves).
//
// The switch is the office's: end the old repeating invoice in Xero first, then switch here and name the next invoice
// date. The server re-reads Xero at that moment and refuses while the template is still live. Drafts are never
// authorised or sent: each waits in Xero for the office to review and send.
//
// Mounted inside XeroBillingPanel (the Details tab of ContractDetailPage.tsx).
import { useCallback, useEffect, useState } from "react";
import { api } from "../../lib/api";
import type { GuvnorBillingView, GuvnorRunResult, GuvnorSwitchPreview } from "../../lib/api";
import { friendlyApiError } from "../../lib/friendlyApiError";
import { Button } from "../../components/Button";
import { ConfirmButton } from "../../components/ConfirmButton";
import { ModalShell, MODAL_ACTIONS } from "../../components/ModalShell";
import { formatDateNumeric } from "../../lib/format";

const INPUT = "w-full rounded-ele border border-ele-purple-accent bg-ele-field px-3 py-1.5 text-sm text-ele-strong outline-none focus:border-ele-green";
const SMALL_BTN = "rounded-ele border border-ele-purple-accent px-3 py-1.5 font-menu text-xs tracking-wide text-ele-body transition hover:border-ele-green hover:text-ele-strong disabled:opacity-50";

// A calendar DAY the server worked out (YYYY-MM-DD), printed in UTC so it can never move a day.
function day(ymd: string): string {
  const out = formatDateNumeric(`${ymd}T00:00:00.000Z`);
  return out === "-" ? ymd : out;
}

function gbp(n: number | null): string {
  return n == null ? "Not priced" : `£${n.toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

const STATE_WORDS: Record<string, string> = {
  drafted: "Draft raised in Xero",
  failed: "Not raised yet",
  ready: "Being raised",
  skipped: "Skipped",
};

function SwitchOnModal({ contractId, onClose, onSwitched }: { contractId: string; onClose: () => void; onSwitched: () => void }) {
  const [date, setDate] = useState("");
  const [preview, setPreview] = useState<GuvnorSwitchPreview | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function check() {
    if (!date || busy) return;
    setBusy(true);
    setError(null);
    setPreview(null);
    try {
      setPreview(await api.contracts.guvnorBillingSwitchOn(contractId, date, true));
    } catch (err) {
      setError(friendlyApiError(err, "Could not check this contract. Please try again."));
    } finally {
      setBusy(false);
    }
  }

  async function confirm() {
    if (!preview || busy) return;
    setBusy(true);
    setError(null);
    try {
      await api.contracts.guvnorBillingSwitchOn(contractId, preview.firstInvoiceDate, false);
      onSwitched();
    } catch (err) {
      setError(friendlyApiError(err, "Could not switch this contract. Please try again."));
    } finally {
      setBusy(false);
    }
  }

  return (
    <ModalShell ariaLabel="Switch to Guvnor billing" onClose={() => !busy && onClose()} width="max-w-[560px]">
      <h2 className="mb-2 font-heading text-xl text-ele-strong">Switch to Guvnor billing</h2>
      <p className="text-sm text-ele-body">
        First end this contract&rsquo;s repeating invoice in Xero (delete it, or end its schedule). Then give the date of the next invoice: the date
        the old repeating invoice would have raised it. Guvnor raises each invoice from then on as a Xero draft, priced at the contract&rsquo;s price
        on its date.
      </p>
      <label className="mt-3 block text-xs text-ele-body" htmlFor="guvnor-next-invoice">
        Next invoice date
      </label>
      <input
        id="guvnor-next-invoice"
        type="date"
        className={INPUT}
        value={date}
        onChange={(e) => {
          setDate(e.target.value);
          setPreview(null);
        }}
      />
      <div className="mt-3">
        <Button type="button" variant="secondary" size="sm" onClick={() => void check()} disabled={!date || busy}>
          {busy && !preview ? "Checking Xero..." : "Check"}
        </Button>
      </div>
      {error && (
        <p role="alert" className="mt-3 text-sm text-ele-danger">
          {error}
        </p>
      )}
      {preview && (
        <div className="mt-3 rounded-ele border border-ele-purple-accent bg-ele-bg p-3 text-sm text-ele-body">
          <p className="font-semibold text-ele-strong">The first invoices Guvnor will raise</p>
          <ul className="mt-1">
            {preview.firstInvoices.map((f) => (
              <li key={f.invoiceDate}>
                {day(f.invoiceDate)}: {f.heldReason ? `held, ${f.heldReason}` : `${gbp(f.amount)} ex VAT, for ${day(f.invoiceDate)} to ${day(f.periodEnd)}`}
              </li>
            ))}
          </ul>
          {preview.templates.length > 0 && (
            <p className="mt-2 text-xs">
              Linked Xero repeating invoices, as Xero reports them now: {preview.templates.map((t) => `${t.reference ?? "unnamed"} (${t.status ?? "unknown"})`).join(", ")}.
            </p>
          )}
        </div>
      )}
      <div className={MODAL_ACTIONS}>
        <Button type="button" variant="secondary" size="sm" onClick={onClose} disabled={busy}>
          Cancel
        </Button>
        {preview && (
          <ConfirmButton
            onConfirm={() => void confirm()}
            label="Switch to Guvnor billing"
            confirmLabel={`Switch, first invoice ${day(preview.firstInvoiceDate)}?`}
            busy={busy}
            busyLabel="Switching..."
            className={SMALL_BTN}
          />
        )}
      </div>
    </ModalShell>
  );
}

export function GuvnorBillingSection({ contractId, onChanged }: { contractId: string; onChanged: () => void }) {
  const [view, setView] = useState<GuvnorBillingView | null>(null);
  const [loadError, setLoadError] = useState(false);
  const [switchOpen, setSwitchOpen] = useState(false);
  const [run, setRun] = useState<GuvnorRunResult | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [offReason, setOffReason] = useState("");

  const load = useCallback(() => {
    setLoadError(false);
    api.contracts
      .guvnorBilling(contractId)
      .then(setView)
      .catch(() => setLoadError(true));
  }, [contractId]);

  useEffect(() => {
    load();
  }, [load]);

  async function act(key: string, fn: () => Promise<unknown>, fallback: string) {
    if (busy) return;
    setBusy(key);
    setError(null);
    try {
      await fn();
      load();
      onChanged();
    } catch (err) {
      setError(friendlyApiError(err, fallback));
    } finally {
      setBusy(null);
    }
  }

  if (loadError) {
    return (
      <div className="mt-4 flex items-center gap-3">
        <p role="alert" className="text-sm text-ele-danger">
          Could not load Guvnor billing.
        </p>
        <button type="button" onClick={load} className={SMALL_BTN}>
          Retry
        </button>
      </div>
    );
  }
  if (!view) return null;

  if (!view.billedByGuvnor) {
    return (
      <div className="mt-4 border-t border-ele-purple-accent pt-3">
        <h3 className="font-heading text-base text-ele-strong">Guvnor billing</h3>
        <p className="mt-1 text-sm text-ele-body">
          Guvnor can raise this contract&rsquo;s invoices itself instead of a Xero repeating invoice, so a price change is billed from its date with no
          editing in Xero. Each invoice is a Xero draft for the office to review and send.
        </p>
        <div className="mt-2">
          <Button type="button" variant="secondary" size="sm" onClick={() => setSwitchOpen(true)}>
            Switch to Guvnor billing
          </Button>
        </div>
        {switchOpen && (
          <SwitchOnModal
            contractId={contractId}
            onClose={() => setSwitchOpen(false)}
            onSwitched={() => {
              setSwitchOpen(false);
              load();
              onChanged();
            }}
          />
        )}
      </div>
    );
  }

  return (
    <div className="mt-4 border-t border-ele-purple-accent pt-3">
      <h3 className="font-heading text-base text-ele-strong">Guvnor billing</h3>
      <p className="mt-1 text-sm text-ele-body">
        Billed by Guvnor since the invoice of {view.scheduleStart ? day(view.scheduleStart) : "an unknown date"}. Each invoice is raised as a Xero
        draft up to a week before its date, priced at the contract&rsquo;s price on that date.
        {!view.automaticDraftsOn && " Automatic Xero drafts are switched off in Settings, so the daily run raises nothing: use Raise what is due."}
      </p>
      <p className="mt-1 text-xs text-ele-body opacity-70">
        {view.lastRunAt ? `Daily run last finished ${day(view.lastRunAt.slice(0, 10))}.` : "The daily run has not run yet."}
        {view.lastRunNote ? ` ${view.lastRunNote}` : ""}
      </p>

      {view.upcoming.length > 0 && (
        <table className="ele-list-table mt-2 w-full text-sm">
          <thead>
            <tr>
              <th className="text-left">Next invoices</th>
              <th className="text-left">Covers</th>
              <th className="text-right">Ex VAT</th>
            </tr>
          </thead>
          <tbody>
            {view.upcoming.map((u) => (
              <tr key={u.invoiceDate}>
                <td>{day(u.invoiceDate)}</td>
                <td>to {day(u.periodEnd)}</td>
                <td className="text-right">{u.heldReason ? "Held" : gbp(u.amount)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {view.ledger.length > 0 && (
        <table className="ele-list-table mt-3 w-full text-sm">
          <thead>
            <tr>
              <th className="text-left">Raised</th>
              <th className="text-left">State</th>
              <th className="text-right">Ex VAT</th>
              <th className="text-right">Action</th>
            </tr>
          </thead>
          <tbody>
            {view.ledger.map((r) => (
              <tr key={r.id}>
                <td>
                  {day(r.invoiceDate)}
                  {r.invoiceNumber ? ` (${r.invoiceNumber})` : ""}
                </td>
                <td title={r.error ?? undefined}>{STATE_WORDS[r.status] ?? r.status}</td>
                <td className="text-right">{gbp(r.amount)}</td>
                <td className="text-right">
                  {r.status === "failed" && (
                    <ConfirmButton
                      onConfirm={() => void act(`skip-${r.id}`, () => api.contracts.guvnorBillingDate(contractId, r.id, "skip", "Not to be billed"), "Could not skip this date.")}
                      label="Skip"
                      confirmLabel="Skip this date?"
                      busy={busy === `skip-${r.id}`}
                      busyLabel="Skipping..."
                      className={SMALL_BTN}
                    />
                  )}
                  {r.status === "skipped" && (
                    <ConfirmButton
                      onConfirm={() => void act(`restore-${r.id}`, () => api.contracts.guvnorBillingDate(contractId, r.id, "restore"), "Could not restore this date.")}
                      label="Restore"
                      confirmLabel="Raise it after all?"
                      busy={busy === `restore-${r.id}`}
                      busyLabel="Restoring..."
                      className={SMALL_BTN}
                    />
                  )}
                  {r.status === "drafted" && (
                    <ConfirmButton
                      onConfirm={() => void act(`release-${r.id}`, () => api.contracts.guvnorBillingDate(contractId, r.id, "release"), "Could not release this date.")}
                      label="Deleted in Xero"
                      confirmLabel="Raise it again?"
                      busy={busy === `release-${r.id}`}
                      busyLabel="Checking Xero..."
                      title="Only once the draft has been deleted in Xero: it is raised again at the next run."
                      className={SMALL_BTN}
                    />
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <div className="mt-3 flex flex-wrap items-center gap-2">
        <button
          type="button"
          className={SMALL_BTN}
          disabled={busy != null}
          onClick={() =>
            void (async () => {
              setBusy("run-preview");
              setError(null);
              try {
                setRun(await api.contracts.guvnorBillingRun(contractId, true));
              } catch (err) {
                setError(friendlyApiError(err, "Could not check what is due."));
              } finally {
                setBusy(null);
              }
            })()
          }
        >
          Raise what is due
        </button>
      </div>
      {run && (
        <div className="mt-2 rounded-ele border border-ele-purple-accent bg-ele-bg p-3 text-sm text-ele-body">
          {run.invoices.length === 0 ? (
            <p>Nothing is due yet: an invoice is raised up to a week before its date.</p>
          ) : (
            <>
              <ul>
                {run.invoices.map((i) => (
                  <li key={i.invoiceDate}>
                    {day(i.invoiceDate)}: {gbp(i.amount)} ex VAT{i.detail ? `. ${i.detail}` : ""}
                  </li>
                ))}
              </ul>
              {run.preview && (
                <div className="mt-2">
                  <ConfirmButton
                    onConfirm={() =>
                      void act(
                        "run",
                        async () => {
                          setRun(await api.contracts.guvnorBillingRun(contractId, false));
                        },
                        "Could not raise the drafts.",
                      )
                    }
                    label="Raise as Xero drafts"
                    confirmLabel="Raise these drafts?"
                    busy={busy === "run"}
                    busyLabel="Raising..."
                    className={SMALL_BTN}
                  />
                </div>
              )}
            </>
          )}
        </div>
      )}

      <div className="mt-3">
        <label className="block text-xs text-ele-body" htmlFor="guvnor-off-reason">
          Switch back to a Xero repeating invoice (why)
        </label>
        <div className="mt-1 flex flex-wrap items-center gap-2">
          <input id="guvnor-off-reason" className={`${INPUT} max-w-sm`} value={offReason} onChange={(e) => setOffReason(e.target.value)} />
          <ConfirmButton
            onConfirm={() => void act("off", () => api.contracts.guvnorBillingSwitchOff(contractId, offReason), "Could not switch Guvnor billing off.")}
            label="Switch off"
            confirmLabel="Stop Guvnor billing?"
            busy={busy === "off"}
            busyLabel="Switching off..."
            disabled={offReason.trim().length < 3}
            className={SMALL_BTN}
          />
        </div>
      </div>
      {error && (
        <p role="alert" className="mt-2 text-sm text-ele-danger">
          {error}
        </p>
      )}
    </div>
  );
}
